import DOMPurify from "dompurify";
import { ruleSchema } from "./personalizationSchema";

export const logPersonalizationError = (stage, details) => {
  console.error("[Personalization]", stage, details);
};

// Rule authors write section/content ids as "SEC_494", "494", or 494 —
// normalize both sides to the bare identifier before comparing, rather than
// forcing one format.
const normalizeKey = (value, prefixPattern) => {
  if (value === undefined || value === null) return "";
  const raw = String(value).trim();
  const match = raw.match(prefixPattern);
  return (match ? match[1] : raw).toLowerCase();
};

const normalizeSectionKey = (sectionId) => normalizeKey(sectionId, /^SEC_?(.+)$/i);
const normalizeContentKey = (contentId) => normalizeKey(contentId, /^CONT_?(.+)$/i);

const sectionIdMatches = (ruleSectionId, realSectionId) =>
  normalizeSectionKey(ruleSectionId) === normalizeSectionKey(realSectionId);

const contentIdMatchesItem = (contentId, item) =>
  Boolean(contentId) && normalizeContentKey(contentId) === normalizeContentKey(item?.id);

export const validatePersonalizationConfig = (rawJson) => {
  if (!Array.isArray(rawJson)) {
    logPersonalizationError("invalid-config-shape", { rawJson });
    return { valid: false, rules: [], errors: ["Personalization config must be an array"] };
  }

  const rules = [];
  const errors = [];

  rawJson.forEach((candidate, index) => {
    try {
      const rule = ruleSchema.validateSync(candidate, { stripUnknown: true });
      rules.push(rule);// Only runs if validation succeeds
    } catch (error) {
      logPersonalizationError("rule-validation-failed", { index, candidate, error: error?.message });
      errors.push(`Rule at index ${index}: ${error?.message}`);
    }
  });

  return { valid: true, rules, errors };
};

// The config now lives in an S3 bucket rather than a fixture only engineering
// controls, so whatever ends up in htmlContent has to be treated as
// untrusted — every consumer renders it via dangerouslySetInnerHTML with no
// sanitization of its own. Cleaning it once here, at the one place a raw
// string (or an object that already carries an htmlContent field) becomes
// part of a content item, means none of the ~7 rendering call sites need to
// remember to do it themselves.
const sanitizeHtml = (html) => DOMPurify.sanitize(html);

// A rule's content is either a structured object or a raw HTML string —
// normalize the string case to an explicit field so components have one
// thing to check instead of sniffing the string for tags.
const normalizeContentValue = (rawContent) => {
  if (rawContent && typeof rawContent === "object") {
    return rawContent.htmlContent
      ? { ...rawContent, htmlContent: sanitizeHtml(rawContent.htmlContent) }
      : { ...rawContent };
  }
  if (typeof rawContent === "string") return { htmlContent: sanitizeHtml(rawContent) };
  return {};
};

const isPrependPosition = (position) => {
  const pos = (position || "").toLowerCase();
  return pos === "top" || pos === "left";
};

const sectionOrderBounds = (sectionContentMasters, sectionMasterId) => {
  const orders = sectionContentMasters
    .filter((item) => item?.sectionMasterId === sectionMasterId)
    .map((item) => item?.displayOrder ?? 0);
  return {
    max: orders.length ? Math.max(...orders) : -1,
    min: orders.length ? Math.min(...orders) : 0,
  };
};

// New items are tagged "_personalization" (mirroring the POC's
// data-personalization-section-id DOM marker) so anything injected by a rule
// stays identifiable as rule-sourced. Always folds in ruleIndex/componentIndex,
// not just contentId — real rule files reuse (or omit) contentId across
// sibling components in the same rule, and two new items must never end up
// with the same id.
const buildNewItemId = (contentId, sectionKey, ruleIndex, componentIndex) => {
  const label = contentId && String(contentId).trim() ? contentId : `pers_${sectionKey}`;
  return `${label}_${ruleIndex}_${componentIndex}_personalization`;
};

const buildContentItem = ({ componentRule, sectionKey, sectionMasterId, ruleIndex, componentIndex, displayOrder }) => {
  const content = normalizeContentValue(componentRule?.content);
  return {
    ...content,
    id: buildNewItemId(componentRule?.contentId, sectionKey, ruleIndex, componentIndex),
    sectionMasterId,
    enabled: content?.enabled !== false,
    displayOrder,
    // Not consumed anywhere yet — carried through so it's already on the
    // item once a GA event trigger is built to read it later.
    gaEvent: componentRule?.gaEvent,
    isPersonalized: true,
  };
};

// Content-level slice: root action is blank, so the target section itself is
// untouched and each component[] entry acts independently on its content
// list, using its own action/position/contentId.
const applyContentToSection = (sectionContentMasters, rule, ruleIndex, sectionMasterId, sectionKey) => {
  const components = Array.isArray(rule?.component) ? rule.component : [];
  const items = [...sectionContentMasters];

  components.forEach((componentRule, componentIndex) => {
    const componentAction = (componentRule?.action || "").toLowerCase();
    const anchorIndex = componentRule?.contentId
      ? items.findIndex(
        (item) => item?.sectionMasterId === sectionMasterId && contentIdMatchesItem(componentRule.contentId, item)
      )
      : -1;

    const content = normalizeContentValue(componentRule?.content);

    // A matching contentId updates that item in place, unless the rule
    // explicitly says "add" — then it's inserted alongside the match rather
    // than overwriting it.
    if (anchorIndex > -1 && componentAction !== "add") {
      const existing = items[anchorIndex];
      items[anchorIndex] = {
        ...existing,
        ...content,
        enabled: content?.enabled !== undefined ? content.enabled !== false : existing?.enabled,
        gaEvent: componentRule?.gaEvent ?? existing?.gaEvent,
      };
      return;
    }

    // A contentId that names a real item positions relative to THAT item
    // (immediately left/right of it) — that's the point of naming one. With
    // no contentId, or one that doesn't match anything, there's no anchor to
    // sit next to, so position falls back to being relative to the whole
    // section instead (top/left = its very first item, bottom/right/blank =
    // its very last).
    let displayOrder;
    if (anchorIndex > -1) {
      const anchorOrder = items[anchorIndex]?.displayOrder ?? 0;
      displayOrder = isPrependPosition(componentRule?.position) ? anchorOrder - 0.5 : anchorOrder + 0.5;
    } else {
      const { max, min } = sectionOrderBounds(items, sectionMasterId);
      displayOrder = isPrependPosition(componentRule?.position) ? min - 1 : max + 1;
    }

    items.push(buildContentItem({ componentRule, sectionKey, sectionMasterId, ruleIndex, componentIndex, displayOrder }));
  });

  return items;
};

// Section-level slice: root action is "replace" or "add". This is a render
// concern, not a content-list concern — the whole component[] array is one
// block that overrides how the section renders as a unit (mirrors the POC's
// GetComponent.jsx render-layer behavior), so it's kept separate from
// sectionContentMasters rather than inserted as more list items that each
// section component would then have to special-case to lay out correctly.
//
// A section can be targeted by more than one add/replace rule at once, each
// wanting its own position (e.g. one block on top, a different one on the
// bottom) — those must render independently, not get flattened into a
// single merged block. So overrides are grouped by (section, position,
// sectionType): only rules that share the exact same position AND the same
// sectionType combine their blocks together. Same section and position but
// a different sectionType is always a separate entry, never merged — two
// rules can legitimately target the same spot with two different kinds of
// content.
const mergeSectionOverrideList = (existingList, rule, sectionMasterId) => {
  const list = existingList ? [...existingList] : [];
  const position = (rule?.position || "").toLowerCase();
  const sectionType = (rule?.sectionType || "").toLowerCase().trim();
  const components = Array.isArray(rule?.component) ? rule.component : [];
  const blocks = components.map((componentRule) => ({
    ...normalizeContentValue(componentRule?.content),
    // Not consumed anywhere yet — see buildContentItem above.
    gaEvent: componentRule?.gaEvent,
  }));
  const title = rule?.title || rule?.Title || "";

  const existingIndex = list.findIndex((entry) => entry.position === position && entry.sectionType === sectionType);
  if (existingIndex > -1) {
    const existing = list[existingIndex];
    list[existingIndex] = {
      ...existing,
      action: existing.action || (rule?.action || "").toLowerCase(),
      title: existing.title || title,
      fullWidth: rule?.fullWidth !== undefined ? Boolean(rule.fullWidth) : existing.fullWidth,
      blocks: [...existing.blocks, ...blocks],
    };
  } else {
    list.push({
      sectionMasterId,
      action: (rule?.action || "").toLowerCase(),
      position,
      sectionType,
      title,
      fullWidth: Boolean(rule?.fullWidth),
      blocks,
    });
  }

  return list;
};

// Single source of truth for merging personalization rules into the base
// Home Page data. Every rule is processed independently — a bad record is
// logged and skipped, the rest still apply. Returns sectionContentMasters
// (content-level changes, ready to render through existing components
// unmodified) alongside sectionOverrides (section-level changes, keyed by
// real section id, each an array of independently-positioned override
// entries for the render layer to apply around/instead of its normal
// output for that section).
export const applyPersonalization = (base, rules) => {
  const sectionMasters = Array.isArray(base?.sectionMasters) ? base.sectionMasters : [];
  let sectionContentMasters = Array.isArray(base?.sectionContentMasters) ? [...base.sectionContentMasters] : [];
  const sectionOverrides = {};

  (rules || []).forEach((rule, ruleIndex) => {
    try {
      const anchor = sectionMasters.find((section) => sectionIdMatches(rule?.sectionId, section?.id));
      if (!anchor) {
        logPersonalizationError("section-not-found", { sectionId: rule?.sectionId });
        return;
      }

      const rootAction = (rule?.action || "").toLowerCase();

      if (rootAction === "replace" || rootAction === "add") {
        sectionOverrides[anchor.id] = mergeSectionOverrideList(sectionOverrides[anchor.id], rule, anchor.id);
        return;
      }

      const sectionKey = normalizeSectionKey(rule?.sectionId);
      sectionContentMasters = applyContentToSection(sectionContentMasters, rule, ruleIndex, anchor.id, sectionKey);
    } catch (error) {
      logPersonalizationError("rule-apply-failed", { ruleIndex, rule, error: error?.message });
    }
  });

  return { sectionMasters, sectionContentMasters, sectionOverrides };
};
